// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import clearSearch from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isFilterOpen: state.pages.savedOrderList.isFilterOpen,
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: getSession(state).language,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
};

// XNG-Changes
enum fields {
    showTagsOnly = "showTagsOnly",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    // XNG-Changes:
    savedCartNameGridItem?: GridItemProps;
    savedCartNameTextField?: TextFieldPresentationProps;
}

export const savedOrderListFilterStyles: SavedOrderListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    datesContainer: { gap: 10 },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    fromGridItem: { width: 6 },
    toGridItem: { width: 6 },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    orderTotalContainer: { gap: 10 },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: 6 },
    orderTotalAmountGridItem: { width: 6 },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = savedOrderListFilterStyles;
let updateTimeoutId: NodeJS.Timeout;
const SavedOrderListFilter = ({
    isFilterOpen,
    getCartsApiParameter,
    updateSearchFields,
    clearSearch,
    language,
    fields,
}: Props) => {
    const [orderSubtotal, setOrderSubtotal] = React.useState("");
    const [orderSubtotalOperator, setOrderSubtotalOperator] = React.useState("");
    // XNG-Changes:
    const [savedCartName, setSavedCartName] = React.useState("");

    useEffect(() => {
        if (
            !orderSubtotal &&
            !orderSubtotalOperator &&
            getCartsApiParameter.orderTotal &&
            getCartsApiParameter.orderTotalOperator
        ) {
            setOrderSubtotal(getCartsApiParameter.orderTotal);
            setOrderSubtotalOperator(getCartsApiParameter.orderTotalOperator);
        }
    }, []);

    // XNG-Change:
    useEffect(() => {
        if (!getCartsApiParameter.orderTotal && !getCartsApiParameter.orderTotalOperator) {
            setOrderSubtotal("");
            setOrderSubtotalOperator("");
        }
    }, [getCartsApiParameter.orderTotal, getCartsApiParameter.orderTotalOperator]);

    // XNG-Changes:
    useEffect(() => {
        if (!(getCartsApiParameter as any).savedCartName) {
            setSavedCartName("");
        }
    }, [(getCartsApiParameter as any).savedCartName]);

    const orderSubtotalChangeHandler = (totalValue: string, totalOperator: string) => {
        if ((totalValue === "" && totalOperator !== "") || (totalValue !== "" && totalOperator === "")) {
            return;
        }

        updateSearchFields({
            // XNG-Changes
            orderTotal: totalValue,
            orderTotalOperator: totalOperator,
        });
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const orderSubtotalValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const total = event.target.value;

        setOrderSubtotal(total);

        clearTimeout(updateTimeoutId);

        updateTimeoutId = setTimeout(() => {
            orderSubtotalChangeHandler(total, orderSubtotalOperator);
        }, 350);
    };

    const orderSubtotalOperatorChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const operator = event.target.value;
        setOrderSubtotalOperator(operator);

        orderSubtotalChangeHandler(orderSubtotal, operator);
    };

    const resetOrderSubtotal = () => {
        setOrderSubtotal("");
        setOrderSubtotalOperator("");
        /**
         * XNG-Changes:
         * OOTB issue: when the tag is removed, the filter input is not cleared
         */
        updateSearchFields({ orderTotalOperator: "", orderTotal: "" });
        // updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };
    // XNG-Changes
    const savedCartNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSavedCartName(event.target.value);
        updateSearchFields({
            savedCartName: event.target.value,
        } as any);
    };

    const resetCartName = () => {
        setSavedCartName("");
        updateSearchFields({ savedCartName: "" } as any);
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setSavedCartName("");
        setOrderSubtotal("");
        setOrderSubtotalOperator("");
        resetCartName();
    };

    // XNG-Changes: adding showTagsOnly condition
    if (fields.showTagsOnly) {
        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {getCartsApiParameter.fromDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            fromDateChangeHandler({});
                        }}
                    >
                        {`${translate("From")}: ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(getCartsApiParameter.fromDate)!,
                            language,
                        })}`}
                    </Tag>
                )}
                {getCartsApiParameter.toDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            toDateChangeHandler({});
                        }}
                    >
                        {`${translate("To")}: ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(getCartsApiParameter.toDate)!,
                            language,
                        })}`}
                    </Tag>
                )}
                {getCartsApiParameter.orderTotalOperator && getCartsApiParameter.orderTotal && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            resetOrderSubtotal();
                        }}
                    >
                        {`${translate("Cart Total")}: ${getCartsApiParameter.orderTotalOperator} ${
                            getCartsApiParameter.orderTotal
                        }`}
                    </Tag>
                )}
                {(getCartsApiParameter as any).savedCartName && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            resetCartName();
                        }}
                    >
                        {`${translate("Cart Name")}: ${(getCartsApiParameter as any).savedCartName}`}
                    </Tag>
                )}
            </StyledWrapper>
        );
    }

    const fromDate = getCartsApiParameter.fromDate ? new Date(getCartsApiParameter.fromDate) : undefined;
    const toDate = getCartsApiParameter.toDate ? new Date(getCartsApiParameter.toDate) : undefined;
    const savedCart = (getCartsApiParameter as any).savedCartName || "";

    // XNG-Changes: adding new Date(), show search tags always if toggle is in open and closed status
    return (
        <>
            <GridContainer {...styles.container}>
                {/* <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.heading} as="h2">
                        {translate("Filter")}
                    </Typography>
                </GridItem> */}

                {/* XNG-Changes: added saved cart name search filter */}
                <GridItem {...styles.savedCartNameGridItem}>
                    <TextField
                        {...styles.savedCartNameTextField}
                        label={translate("Cart Name")}
                        placeholder={translate("Enter Cart Name")}
                        value={savedCart}
                        maxLength={50}
                        type="text"
                        data-test-selector="savedOrderList_cartName"
                        onChange={savedCartNameChangeHandler}
                    />
                </GridItem>
                <GridItem {...styles.datesGridItem}>
                    <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                        <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                        <GridContainer {...styles.datesContainer}>
                            <GridItem data-test-selector="savedOrderList_fromDateFilter" {...styles.fromGridItem}>
                                <DatePicker
                                    {...styles.fromDate}
                                    label={translate("From")}
                                    selectedDay={fromDate}
                                    onDayChange={fromDateChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        maxDate: toDate || new Date(),
                                        ...styles.fromDate?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                            <GridItem data-test-selector="savedOrderList_toDateFilter" {...styles.toGridItem}>
                                <DatePicker
                                    {...styles.toDate}
                                    label={translate("To")}
                                    selectedDay={toDate}
                                    onDayChange={toDateChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        minDate: fromDate,
                                        maxDate: new Date(),
                                        ...styles.toDate?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledDateRangeFieldSet>
                </GridItem>
                <GridItem {...styles.orderTotalGridItem}>
                    <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                        <VisuallyHidden as="label">{translate("Cart Subtotal")}</VisuallyHidden>
                        <GridContainer {...styles.orderTotalContainer}>
                            <GridItem {...styles.orderTotalOperatorGridItem}>
                                <Select
                                    {...styles.orderTotalOperatorSelect}
                                    label={translate("Cart Subtotal")}
                                    value={orderSubtotalOperator}
                                    data-test-selector="savedOrderList_comparisonMethod"
                                    onChange={orderSubtotalOperatorChangeHandler}
                                >
                                    <option value="">{translate("Select")}</option>
                                    <option key="Greater Than" value="Greater Than">
                                        {translate("Greater Than")}
                                    </option>
                                    <option key="Less Than" value="Less Than">
                                        {translate("Less Than")}
                                    </option>
                                    <option key="Equal To" value="Equal To">
                                        {translate("Equal To")}
                                    </option>
                                </Select>
                            </GridItem>
                            <GridItem {...styles.orderTotalAmountGridItem}>
                                <TextField
                                    {...styles.orderTotalAmountTextField}
                                    value={orderSubtotal}
                                    placeholder={translate("Enter Amount")}
                                    aria-label={translate("Order Subtotal Value")}
                                    data-test-selector="savedOrderList_comparisonValue"
                                    onChange={orderSubtotalValueChangeHandler}
                                    // XNG-Changes
                                    type="amount"
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledOrderTotalFieldSet>
                </GridItem>
                <GridItem {...styles.buttonsItem}>
                    <Button
                        {...styles.clearFiltersButton}
                        data-test-selector="savedOrderList_clearFiltersButton"
                        onClick={clearFiltersClickHandler}
                        aria-label="Clear Filters"
                    >
                        {translate("Clear Filters")}
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListFilter),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Filter",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
